import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import {
  LayoutDashboard, Users, Bell, MessageSquare, FileText,
  Calendar, Settings, LogOut, ChevronLeft, ChevronRight, X,
  ClipboardList, Stethoscope, BookOpen, ClipboardCheck, Zap, ListTodo
} from "lucide-react";
import api from "@/api";

// Premium HANA Logo Component
function HanaLogo({ collapsed }) {
  if (collapsed) {
    return (
      <div className="flex items-center justify-center">
        <span className="text-emerald-600 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>H</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-bold text-slate-800 text-xl tracking-tight font-display">Hana</span>
      <span className="text-emerald-400 text-lg font-light">|</span>
      <span className="font-semibold text-slate-600 text-lg" style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}>هناء</span>
    </div>
  );
}

const NAV_ITEMS = {
  IDE: [
    { path: "/nurse/dashboard", icon: LayoutDashboard, key: "nav.dashboard" },
    { path: "/nurse/workqueue", icon: ListTodo, key: "nav.workqueue" },
    { path: "/nurse/patients", icon: Users, key: "nav.patients" },
    { path: "/nurse/messages", icon: MessageSquare, key: "nav.messages" },
    { path: "/nurse/appointments", icon: Calendar, key: "nav.appointments" },
    { path: "/nurse/education", icon: BookOpen, key: "nav.education" },
  ],
  MEDECIN: [
    { path: "/doctor/dashboard", icon: LayoutDashboard, key: "nav.dashboard" },
    { path: "/doctor/patients", icon: Users, key: "nav.patients" },
    { path: "/doctor/messages", icon: MessageSquare, key: "nav.messages" },
    { path: "/doctor/appointments", icon: Calendar, key: "nav.appointments" },
  ],
  SECRETAIRE_MEDICAL: [
    { path: "/secretary/workqueue", icon: ListTodo, key: "nav.workqueueSecretary" },
    { path: "/secretary/medical", icon: Calendar, key: "nav.dashboard" },
    { path: "/secretary/messages", icon: MessageSquare, key: "nav.messages" },
  ],
  ADMIN_CLINIQUE: [
    { path: "/admin/clinic", icon: LayoutDashboard, key: "nav.dashboard" },
  ],
  ADMIN_GROUPE: [
    { path: "/admin/group", icon: LayoutDashboard, key: "nav.dashboard" },
  ],
  SUPER_ADMIN_SAAS: [
    { path: "/admin/super", icon: LayoutDashboard, key: "nav.dashboard" },
    { path: "/admin/clinic", icon: LayoutDashboard, key: "nav.clinicAdmin" },
  ],
};

export default function Sidebar({ isMobileOpen = false, onMobileClose = () => {} }) {
  const { user, logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState({
    newAlerts: 0,
    alertsBadge: 0,
    doctorAlertsBadge: 0,
    unreadMessages: 0,
    criticalPatients: 0,
    secretaryTasks: 0,
    partnerOrders: 0,
  });

  // Fetch notification counts
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/kpi/notifications");
      setNotifications(res.data);
    } catch {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    if (user && ["IDE", "MEDECIN", "ADMIN_CLINIQUE", "SECRETAIRE_MEDICAL"].includes(user.role)) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Mark alerts as seen when navigating to dashboard (where alerts are shown)
  useEffect(() => {
    const alertPages = ["/nurse/dashboard", "/doctor/dashboard"];
    if (alertPages.some(p => location.pathname.includes(p))) {
      api.post("/kpi/professional/mark-viewed?section=alerts").then(fetchNotifications).catch(() => {});
    }
  }, [location.pathname, fetchNotifications]);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const navItems = NAV_ITEMS[user?.role] || [];

  // Get badge count for a specific path - ONLY action-required items
  const getBadgeCount = (path) => {
    if (user?.role === "IDE") {
      // Dashboard: new alerts
      if (path.includes("/dashboard")) return notifications.alertsBadge || 0;
      // Messages: unread conversations
      if (path.includes("/messages")) return notifications.unreadMessages || 0;
    }
    if (user?.role === "MEDECIN") {
      // Dashboard: pending validations + escalated alerts
      if (path.includes("/dashboard")) return notifications.doctorAlertsBadge || 0;
      // Messages: unread conversations
      if (path.includes("/messages")) return notifications.unreadMessages || 0;
    }
    if (user?.role === "SECRETAIRE_MEDICAL") {
      if (path.includes("/secretary/medical")) return notifications.secretaryTasks || 0;
      if (path.includes("/messages")) return notifications.unreadMessages || 0;
    }
    return 0;
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          data-testid="sidebar-backdrop"
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-30 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      <aside
        data-testid="sidebar"
        className={`
          fixed left-0 top-0 h-full bg-white/90 backdrop-blur-xl border-r border-slate-200/40
          flex flex-col z-40
          ${collapsed ? "w-20" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        style={{ transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1), transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-100/80">
        <HanaLogo collapsed={collapsed} />
        {/* Mobile close button (only on mobile) */}
        <button
          onClick={onMobileClose}
          data-testid="sidebar-mobile-close"
          className="lg:hidden p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="hidden lg:flex p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600"
            style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2} />
          </button>
        )}
        {collapsed && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="hidden lg:flex absolute -right-3 top-8 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center text-slate-400 hover:text-slate-600 shadow-sm"
            style={{ transition: 'color 0.2s ease' }}
          >
            <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
          </button>
        )}
      </div>

      {/* User info */}
      {!collapsed && user && (
        <div className="px-5 py-4 border-b border-slate-100/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm border border-emerald-200/40">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 text-sm truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-xs text-slate-500 truncate">{t(`roles.${user.role}`)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <div className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            const badgeCount = getBadgeCount(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.key.replace("nav.", "")}`}
                className={`
                  relative flex items-center gap-3 px-4 py-3 rounded-xl font-medium
                  ${active
                    ? "bg-emerald-50/80 text-emerald-700"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  }
                  ${collapsed ? "justify-center px-0" : ""}
                `}
                style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}
              >
                {active && !collapsed && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-7 bg-emerald-600 rounded-r-full" />
                )}
                <div className="relative">
                  <Icon 
                    className={`w-5 h-5 flex-shrink-0 ${active ? "text-primary" : ""}`} 
                    strokeWidth={active ? 2 : 1.5} 
                  />
                  {badgeCount > 0 && collapsed && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <span className="text-sm flex-1">{item.labelFallback && t(item.key) === item.key ? item.labelFallback : t(item.key)}</span>
                )}
                {!collapsed && badgeCount > 0 && (
                  <span className="min-w-[22px] h-[22px] bg-rose-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom section */}
      <div className="p-4 border-t border-slate-100/80">
        {/* Language toggle */}
        {!collapsed && (
          <div className="flex gap-1 p-1 bg-slate-100/80 rounded-xl mb-3">
            <button
              onClick={() => changeLanguage("fr")}
              data-testid="lang-fr"
              className={`flex-1 text-xs py-2 rounded-lg font-medium ${
                language === "fr" 
                  ? "bg-white text-primary shadow-sm" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              style={{ transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease' }}
            >
              FR
            </button>
            <button
              onClick={() => changeLanguage("ar")}
              data-testid="lang-ar"
              className={`flex-1 text-xs py-2 rounded-lg font-medium ${
                language === "ar" 
                  ? "bg-white text-primary shadow-sm" 
                  : "text-slate-500 hover:text-slate-700"
              }`}
              style={{ transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease' }}
            >
              AR
            </button>
          </div>
        )}

        <button
          onClick={logout}
          data-testid="logout-btn"
          className={`
            flex items-center gap-3 w-full px-4 py-3 rounded-xl text-slate-500 
            hover:bg-rose-50 hover:text-rose-600 font-medium
            ${collapsed ? "justify-center px-0" : ""}
          `}
          style={{ transition: 'background-color 0.2s ease, color 0.2s ease' }}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span className="text-sm">{t("auth.logout")}</span>}
        </button>
      </div>
      </aside>
    </>
  );
}
